// src/pages/ELearning/TestResultPage.tsx — DESIGN v2
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import {
  Award, CheckCircle, XCircle, ArrowLeft, RefreshCw, Download,
  Trophy, Flame, Target, Sparkles, ChevronDown, ChevronUp, Clock, Star, ShieldCheck, Copy
} from 'lucide-react';

interface AnswerDetail {
  question: {
    _id: string;
    questionText: string;
    questionType: 'single' | 'multiple' | 'text';
    options: { text: string; isCorrect: boolean }[];
    correctAnswer?: string;
    points: number;
  };
  selectedOptions?: string[];
  textAnswer?: string;
  isCorrect: boolean;
  pointsEarned: number;
}

interface ResultData {
  result: {
    _id: string;
    test: { _id: string; title: string };
    score: number;
    totalPoints: number;
    passed: boolean;
    answers: AnswerDetail[];
  };
  pointsGained: number;
  newRating: number;
  streak: number;
}

const TestResultPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { testId } = useParams<{ testId: string }>();
  const state = location.state as { result?: ResultData } | null;
  const result = state?.result;

  const retakeTestId = result?.result?.test?._id || testId;

  const [downloadingCert, setDownloadingCert] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false); // VERIFYFIX

  const handleCopyVerifyLink = async () => {
    const certNum = (result as any)?.result?.certificateNumber;
    if (!certNum) {
      showToast('Номер сертификата ещё не присвоен. Сначала скачайте сертификат.', 'info');
      return;
    }
    const url = window.location.origin + '/certificate/verify/' + certNum;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };
  const [showDetails, setShowDetails] = useState(false);
  const [animatedScore, setAnimatedScore] = useState(0);

  const score = result?.result?.score || 0;
  const passed = result?.result?.passed || false;

  // ─── Анимация счётчика ───
  useEffect(() => {
    let raf: number;
    const start = performance.now();
    const duration = 1200;
    const animate = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / duration, 1);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setAnimatedScore(Math.round(score * eased * 10) / 10);
      if (t < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [score]);

  const handleDownloadCertificate = async () => {
    const resultId = result?.result?._id;
    if (!resultId) {
      showToast('ID результата не найден', 'error');
      return;
    }
    setDownloadingCert(true);
    try {
      const token = localStorage.getItem('token')
                 || localStorage.getItem('accessToken')
                 || localStorage.getItem('authToken');
      const res = await fetch(
        `/api/elearning/results/${resultId}/certificate`,
        { headers: token ? { Authorization: 'Bearer ' + token } : {} }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showToast('Ошибка: ' + (err.error || res.status), 'error');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificate-${resultId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Certificate error:', e);
      const msg = e instanceof Error ? e.message : String(e);
      showToast('Ошибка скачивания: ' + msg, 'error');
    } finally {
      setDownloadingCert(false);
    }
  };

  if (!result) {
    return (
      <>
        <style>{`@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }`}</style>
        <div style={styles.errorContainer}>
          <div style={styles.errorIconWrap}>
            <XCircle size={56} color="#fff" />
          </div>
          <h2 style={styles.errorTitle}>Результаты не найдены</h2>
          <p style={{ color: '#8b8fa8', fontSize: 14 }}>Возможно, вы попали на эту страницу напрямую.</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 24, flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/app/elearning/tests')} style={styles.backButton}>
              <ArrowLeft size={16} /> К тестам
            </button>
            {retakeTestId && (
              <button
                onClick={() => navigate(`/app/elearning/tests/${retakeTestId}`)}
                style={styles.retakeButton}
              >
                <RefreshCw size={16} /> Пройти заново
              </button>
            )}
          </div>
        </div>
      </>
    );
  }

  const { result: testResult, pointsGained, newRating, streak } = result;
  const answers = testResult?.answers || [];
  const totalPoints = testResult?.totalPoints || 0;
  const correctCount = answers.filter(a => a.isCorrect).length;

  // Круговой gauge
  const RADIUS = 90;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const dashOffset = CIRCUMFERENCE * (1 - animatedScore / 100);

  const gradStart = passed ? '#11998e' : '#ef4444';
  const gradEnd = passed ? '#38ef7d' : '#dc2626';
  const accentGradient = passed
    ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
    : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pop { 0% { transform: scale(0); } 60% { transform: scale(1.15); } 100% { transform: scale(1); } }
        @keyframes confetti-fall {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(400px) rotate(720deg); opacity: 0; }
        }
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        .rp-hero { animation: fadeUp 0.5s ease-out; }
        .rp-kpi { animation: fadeUp 0.5s ease-out backwards; transition: all 0.3s ease; }
        .rp-kpi:hover { transform: translateY(-4px); box-shadow: 0 16px 40px rgba(31, 38, 135, 0.14) !important; }
        .rp-kpi:nth-child(1) { animation-delay: 0.1s; }
        .rp-kpi:nth-child(2) { animation-delay: 0.15s; }
        .rp-kpi:nth-child(3) { animation-delay: 0.2s; }
        .rp-kpi:nth-child(4) { animation-delay: 0.25s; }
        .rp-btn { transition: all 0.3s ease; }
        .rp-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(102, 126, 234, 0.35) !important; }
        .rp-cert-btn { transition: all 0.3s ease; }
        .rp-cert-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(201, 162, 39, 0.5) !important; }
        .rp-trophy { animation: float 3s ease-in-out infinite; }
        .rp-confetti {
          position: absolute;
          width: 10px;
          height: 10px;
          border-radius: 2px;
          animation: confetti-fall 2.5s ease-in forwards;
        }
        .rp-details-toggle { transition: all 0.3s ease; }
        .rp-details-toggle:hover { background: rgba(102, 126, 234, 0.08) !important; }
        .rp-answer { animation: fadeUp 0.4s ease-out backwards; }
      `}</style>

      <div style={styles.container}>
        {/* ═══ Header ═══ */}
        <div style={styles.header}>
          <button onClick={() => navigate('/app/elearning/tests')} style={styles.backLink}>
            <ArrowLeft size={16} /> К списку тестов
          </button>
          {retakeTestId && (
            <button onClick={() => navigate(`/app/elearning/tests/${retakeTestId}`)} className="rp-btn" style={styles.retakeButton}>
              <RefreshCw size={16} /> Пройти заново
            </button>
          )}
        </div>

        {/* ═══ HERO RESULT ═══ */}
        <div className="rp-hero" style={styles.heroCard}>
          {/* Confetti при passed */}
          {passed && (
            <div style={styles.confettiWrap}>
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="rp-confetti"
                  style={{
                    left: `${Math.random() * 100}%`,
                    background: ['#667eea', '#764ba2', '#f6d365', '#fda085', '#11998e', '#38ef7d', '#f5576c'][i % 7],
                    animationDelay: `${Math.random() * 1.5}s`,
                    animationDuration: `${2 + Math.random() * 1.5}s`,
                  }}
                />
              ))}
            </div>
          )}

          <div style={styles.heroContent}>
            {/* Круговой gauge */}
            <div style={styles.gaugeWrap}>
              <svg width="220" height="220" style={styles.gaugeSvg}>
                <defs>
                  <linearGradient id="scoreGradient" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={gradStart} />
                    <stop offset="100%" stopColor={gradEnd} />
                  </linearGradient>
                </defs>
                {/* Track */}
                <circle
                  cx="110"
                  cy="110"
                  r={RADIUS}
                  stroke="rgba(0,0,0,0.06)"
                  strokeWidth="14"
                  fill="none"
                />
                {/* Progress */}
                <circle
                  cx="110"
                  cy="110"
                  r={RADIUS}
                  stroke="url(#scoreGradient)"
                  strokeWidth="14"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={CIRCUMFERENCE}
                  strokeDashoffset={dashOffset}
                  transform="rotate(-90 110 110)"
                  style={{ transition: 'stroke-dashoffset 0.1s linear' }}
                />
              </svg>
              <div style={styles.gaugeInner}>
                <div style={styles.gaugeScore}>{animatedScore.toFixed(0)}<span style={styles.gaugePercent}>%</span></div>
                <div style={styles.gaugeLabel}>
                  {passed ? 'Отличный результат!' : 'Есть куда расти'}
                </div>
              </div>
            </div>

            <div style={styles.heroRight}>
              <div style={styles.heroIconWrap}>
                <div className="rp-trophy" style={{ ...styles.trophyCircle, background: accentGradient }}>
                  {passed ? <Trophy size={36} color="#fff" /> : <Target size={36} color="#fff" />}
                </div>
              </div>

              <h1 style={styles.testTitle}>{testResult.test?.title || 'Тест'}</h1>

              <div style={styles.statusBadge}>
                {passed ? (
                  <span style={styles.passedBadge}>
                    <CheckCircle size={16} /> Тест пройден
                  </span>
                ) : (
                  <span style={styles.failedBadge}>
                    <XCircle size={16} /> Тест не пройден
                  </span>
                )}
              </div>

              <p style={styles.heroSubtitle}>
                {passed
                  ? `Вы ответили правильно на ${correctCount} из ${answers.length} вопросов. Так держать! 🎉`
                  : `Правильных ответов: ${correctCount} из ${answers.length}. Попробуйте ещё раз! 💪`}
              </p>

              {passed && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    onClick={handleDownloadCertificate}
                    disabled={downloadingCert}
                    className="rp-cert-btn"
                    style={styles.certButton}
                  >
                    <Download size={18} />
                    {downloadingCert ? 'Генерация...' : 'Скачать сертификат PDF'}
                  </button>
                  {/* VERIFYFIX: кнопка копирования ссылки проверки */}
                  <button
                    onClick={handleCopyVerifyLink}
                    className="rp-btn"
                    style={styles.verifyButton}
                    title="Скопировать публичную ссылку для проверки сертификата"
                  >
                    {copiedLink ? (
                      <>
                        <CheckCircle size={18} />
                        Скопировано!
                      </>
                    ) : (
                      <>
                        <ShieldCheck size={18} />
                        Ссылка для проверки
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ═══ KPI GRID ═══ */}
        <div style={styles.kpiGrid}>
          <KPI
            icon={<Award size={22} color="#f59e0b" />}
            value={totalPoints}
            label="Баллов за тест"
            accent="linear-gradient(135deg, #f6d365 0%, #fda085 100%)"
            bg="rgba(245, 158, 11, 0.12)"
          />
          <KPI
            icon={<Sparkles size={22} color="#10b981" />}
            value={`+${pointsGained || 0}`}
            label="Бонус получен"
            accent="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
            bg="rgba(16, 185, 129, 0.12)"
          />
          <KPI
            icon={<Star size={22} color="#667eea" />}
            value={newRating || 0}
            label="Новый рейтинг"
            accent="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            bg="rgba(102, 126, 234, 0.12)"
          />
          <KPI
            icon={<Flame size={22} color="#ef4444" />}
            value={streak || 0}
            label="Дней в серии"
            accent="linear-gradient(135deg, #f5576c 0%, #f093fb 100%)"
            bg="rgba(239, 68, 68, 0.12)"
          />
        </div>

        {/* ═══ DETAILS TOGGLE ═══ */}
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="rp-details-toggle"
          style={styles.detailsToggle}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={styles.detailsIcon}>
              {passed ? <CheckCircle size={20} color="#fff" /> : <Target size={20} color="#fff" />}
            </div>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>
              Детализация ответов ({answers.length})
            </span>
          </div>
          {showDetails ? <ChevronUp size={20} color="#8b8fa8" /> : <ChevronDown size={20} color="#8b8fa8" />}
        </button>

        {showDetails && (
          <div style={styles.answersList}>
            {answers.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#8b8fa8', padding: 40 }}>
                Нет данных по ответам
              </p>
            ) : (
              answers.map((ans, idx) => (
                <AnswerCard key={ans.question?._id || idx} ans={ans} idx={idx} />
              ))
            )}
          </div>
        )}
      </div>
    </>
  );
};

// ═══ KPI КАРТОЧКА ═══
const KPI: React.FC<{
  icon: React.ReactNode;
  value: React.ReactNode;
  label: string;
  accent: string;
  bg: string;
}> = ({ icon, value, label, accent, bg }) => (
  <div
    className="rp-kpi"
    style={{
      background: 'rgba(255, 255, 255, 0.55)',
      backdropFilter: 'blur(24px) saturate(180%)',
      WebkitBackdropFilter: 'blur(24px) saturate(180%)',
      border: '1px solid rgba(255, 255, 255, 0.7)',
      borderRadius: 22,
      padding: 20,
      boxShadow: '0 8px 32px rgba(31, 38, 135, 0.08)',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: accent }} />
    <div style={{
      width: 40, height: 40, borderRadius: 12, background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12,
    }}>
      {icon}
    </div>
    <div style={{
      fontSize: 26, fontWeight: 800, color: '#1a1a2e',
      lineHeight: 1.1, marginBottom: 2, letterSpacing: '-0.5px',
    }}>
      {value}
    </div>
    <div style={{
      fontSize: 11, color: '#8b8fa8', fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: 0.5,
    }}>
      {label}
    </div>
  </div>
);

// ═══ ANSWER CARD ═══
const AnswerCard: React.FC<{ ans: AnswerDetail; idx: number }> = ({ ans, idx }) => {
  const passed = ans.isCorrect;
  return (
    <div
      className="rp-answer"
      style={{ ...styles.answerCard, animationDelay: `${idx * 0.04}s` }}
    >
      <div style={styles.answerHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 10,
            background: passed
              ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
              : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 800, fontSize: 13,
            flexShrink: 0,
          }}>
            {idx + 1}
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: passed ? '#10b981' : '#ef4444' }}>
            {passed ? '✓ Правильно' : '✗ Неправильно'}
          </span>
        </div>
        <div style={{
          padding: '4px 12px', borderRadius: 10,
          background: passed ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
          color: passed ? '#10b981' : '#ef4444',
          fontSize: 12, fontWeight: 800,
        }}>
          {ans.pointsEarned} / {ans.question?.points || 0}
        </div>
      </div>

      <p style={styles.questionTextAnswer}>{ans.question?.questionText}</p>

      {ans.question?.questionType !== 'text' ? (
        <div style={styles.options}>
          {ans.question?.options?.map((opt, i) => {
            const isSelected = ans.selectedOptions?.includes(opt.text);
            const isCorrect = opt.isCorrect;

            let bg = 'rgba(255,255,255,0.5)';
            let border = '1px solid rgba(0,0,0,0.05)';
            let icon = null;

            if (isSelected && isCorrect) {
              bg = 'rgba(16, 185, 129, 0.12)';
              border = '1.5px solid rgba(16, 185, 129, 0.4)';
              icon = <CheckCircle size={16} color="#10b981" />;
            } else if (isSelected && !isCorrect) {
              bg = 'rgba(239, 68, 68, 0.1)';
              border = '1.5px solid rgba(239, 68, 68, 0.4)';
              icon = <XCircle size={16} color="#ef4444" />;
            } else if (!isSelected && isCorrect) {
              bg = 'rgba(16, 185, 129, 0.06)';
              border = '1.5px dashed rgba(16, 185, 129, 0.4)';
              icon = <CheckCircle size={16} color="#10b981" />;
            }

            return (
              <div key={i} style={{ ...styles.option, background: bg, border }}>
                <span style={{ fontSize: 14, color: '#1a1a2e', fontWeight: 500, flex: 1 }}>
                  {opt.text}
                </span>
                {icon}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={styles.textAnswers}>
          <div style={styles.textAnswerRow}>
            <span style={styles.textAnswerLabel}>Ваш ответ:</span>
            <span style={styles.textAnswerValue}>{ans.textAnswer || '—'}</span>
          </div>
          <div style={styles.textAnswerRow}>
            <span style={styles.textAnswerLabel}>Правильный:</span>
            <span style={{ ...styles.textAnswerValue, color: '#10b981', fontWeight: 700 }}>
              {ans.question?.correctAnswer || '—'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { maxWidth: 900, margin: '0 auto', paddingBottom: 60 },

  errorContainer: { textAlign: 'center', padding: 60 },
  errorIconWrap: {
    width: 96, height: 96, borderRadius: 28,
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 20px',
    boxShadow: '0 16px 40px rgba(239, 68, 68, 0.3)',
  },
  errorTitle: { fontSize: 22, fontWeight: 800, color: '#1a1a2e', marginBottom: 8 },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
    flexWrap: 'wrap',
    gap: 12,
  },
  backLink: {
    background: 'rgba(255,255,255,0.7)',
    border: '1px solid rgba(0,0,0,0.06)',
    color: '#5a5f7a',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    fontWeight: 600,
    padding: '8px 14px',
    borderRadius: 12,
  },
  retakeButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    borderRadius: 14,
    padding: '10px 18px',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 700,
    fontSize: 14,
    boxShadow: '0 8px 20px rgba(102, 126, 234, 0.35)',
  },
  backButton: {
    padding: '12px 22px',
    background: 'rgba(255,255,255,0.7)',
    color: '#5a5f7a',
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: 14,
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 14,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },

  heroCard: {
    position: 'relative',
    background: 'rgba(255, 255, 255, 0.55)',
    backdropFilter: 'blur(24px) saturate(180%)',
    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.7)',
    borderRadius: 32,
    padding: 36,
    marginBottom: 28,
    overflow: 'hidden',
    boxShadow: '0 12px 48px rgba(31, 38, 135, 0.1)',
  },
  confettiWrap: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '100%',
    overflow: 'hidden',
    pointerEvents: 'none',
  },
  heroContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 40,
    flexWrap: 'wrap',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 1,
  },
  gaugeWrap: {
    position: 'relative',
    width: 220,
    height: 220,
    flexShrink: 0,
  },
  gaugeSvg: { transform: 'rotate(0deg)' },
  gaugeInner: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    width: 180,
  },
  gaugeScore: {
    fontSize: 52,
    fontWeight: 800,
    background: 'linear-gradient(135deg, #1a1a2e 0%, #667eea 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    lineHeight: 1,
    letterSpacing: '-2px',
  },
  gaugePercent: { fontSize: 24, opacity: 0.6 },
  gaugeLabel: {
    fontSize: 11,
    color: '#8b8fa8',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginTop: 8,
  },
  heroRight: { flex: '1 1 320px', textAlign: 'left', minWidth: 280 },
  heroIconWrap: { marginBottom: 16 },
  trophyCircle: {
    width: 72,
    height: 72,
    borderRadius: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  testTitle: {
    fontSize: 24,
    fontWeight: 800,
    color: '#1a1a2e',
    margin: '0 0 12px',
    letterSpacing: '-0.5px',
    lineHeight: 1.2,
  },
  statusBadge: { marginBottom: 14 },
  passedBadge: {
    background: 'rgba(16, 185, 129, 0.15)',
    color: '#10b981',
    padding: '6px 14px',
    borderRadius: 12,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontWeight: 700,
    fontSize: 13,
  },
  failedBadge: {
    background: 'rgba(239, 68, 68, 0.15)',
    color: '#ef4444',
    padding: '6px 14px',
    borderRadius: 12,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontWeight: 700,
    fontSize: 13,
  },
  heroSubtitle: {
    fontSize: 14,
    color: '#5a5f7a',
    lineHeight: 1.5,
    margin: '0 0 20px',
  },
  verifyButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 16,
    fontWeight: 800,
    fontSize: 14,
    cursor: 'pointer',
    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
    letterSpacing: 0.3,
  },
  certButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #c9a227 0%, #f6d365 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 16,
    fontWeight: 800,
    fontSize: 14,
    cursor: 'pointer',
    boxShadow: '0 8px 24px rgba(201, 162, 39, 0.4)',
    letterSpacing: 0.3,
  },

  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: 14,
    marginBottom: 28,
  },

  detailsToggle: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    background: 'rgba(255, 255, 255, 0.55)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(255, 255, 255, 0.6)',
    borderRadius: 18,
    cursor: 'pointer',
    marginBottom: 16,
    boxShadow: '0 4px 16px rgba(31, 38, 135, 0.05)',
  },
  detailsIcon: {
    width: 36,
    height: 36,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
  },

  answersList: { display: 'flex', flexDirection: 'column', gap: 14 },
  answerCard: {
    background: 'rgba(255, 255, 255, 0.55)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(255, 255, 255, 0.6)',
    borderRadius: 20,
    padding: 20,
    boxShadow: '0 4px 16px rgba(31, 38, 135, 0.05)',
  },
  answerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
    flexWrap: 'wrap',
  },
  questionTextAnswer: {
    fontSize: 15,
    fontWeight: 600,
    color: '#1a1a2e',
    marginBottom: 14,
    lineHeight: 1.5,
  },
  options: { display: 'flex', flexDirection: 'column', gap: 8 },
  option: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 14px',
    borderRadius: 12,
    gap: 8,
  },
  textAnswers: { display: 'flex', flexDirection: 'column', gap: 8 },
  textAnswerRow: {
    display: 'flex',
    gap: 8,
    padding: '10px 14px',
    background: 'rgba(255,255,255,0.5)',
    borderRadius: 12,
    fontSize: 13,
  },
  textAnswerLabel: { color: '#8b8fa8', fontWeight: 600, flexShrink: 0 },
  textAnswerValue: { color: '#1a1a2e', fontWeight: 500, wordBreak: 'break-word' },
};

export default TestResultPage;
